/**
 * Typed subscribe hooks for the admin WebSocket gateway — Wave 2.38 FE
 * (TASK-106a).
 *
 * Wire contract: event NAMES and PAYLOAD shapes live in
 * `@music-app/shared/schemas/ws-events.ts`. We import the name constants so
 * there are zero string literals duplicated across FE/BE — renaming a single
 * constant updates both sides via the shared package.
 *
 * Payloads are validated with `safeParse` at the boundary: even though the
 * server emits trusted data, a stale FE bundle against a newer BE (or vice
 * versa) is exactly the failure mode we caught in Wave 2.12. Logging the
 * Zod issue gives an actionable error in DevTools instead of a crash
 * downstream.
 */

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  accountEventSchema,
  notificationNewEventSchema,
  systemAlertEventSchema,
  WS_EVENT_ACCOUNT_EVENT,
  WS_EVENT_NOTIFICATIONS_NEW,
  WS_EVENT_SYSTEM_ALERT,
  type AccountEvent,
  type NotificationNewEvent,
  type SystemAlertEvent,
} from '@music-app/shared';
import { NOTIFICATIONS_INBOX_KEY } from '@/api/notifications';
import { useSocket } from '@/lib/use-socket';
import { Sentry } from '@/lib/sentry';

/**
 * Server-emitted event names for the bulk runner (Wave 2.66 FE,
 * TASK-049-followup). These are tracked as a follow-up on the BE side
 * (TASK-042-followup) — until the gateway publishes them the listeners
 * below will simply never fire and the existing 2s HTTP poll in
 * `useBulkJobQuery` keeps the progress UI alive. Once BE ships, the same
 * code path lights up without an FE redeploy.
 */
export const WS_EVENT_BULK_PROGRESS = 'bulk.progress' as const;
export const WS_EVENT_BULK_COMPLETE = 'bulk.complete' as const;

function reportInvalidEnvelope(eventName: string, issues: unknown): void {
  Sentry.captureMessage('ws.invalid_envelope', {
    level: 'warning',
    extra: { event: eventName, issues },
  });
}

/**
 * Listen to `notifications.new` and invalidate the inbox query.
 *
 * Behaviour for v0.1.0: the drawer + bell badge keep their 30s / 60s polls
 * as a fallback. The invalidate here is purely additive — it lights up the
 * unread badge "instantly" when the BE pushes a new row, without waiting
 * for the next poll cycle.
 */
export function useNotificationsLiveSubscribe(): void {
  const { socket } = useSocket();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!socket) return undefined;

    const handler = (raw: unknown) => {
      const parsed = notificationNewEventSchema.safeParse(raw);
      if (!parsed.success) {
        reportInvalidEnvelope(WS_EVENT_NOTIFICATIONS_NEW, parsed.error.issues);
        return;
      }
      const _event: NotificationNewEvent = parsed.data;
      void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_INBOX_KEY });
    };

    socket.on(WS_EVENT_NOTIFICATIONS_NEW, handler);
    return () => {
      socket.off(WS_EVENT_NOTIFICATIONS_NEW, handler);
    };
  }, [socket, queryClient]);
}

export interface SystemAlertHandler {
  (alert: SystemAlertEvent): void;
}

/**
 * Listen to `system.alert` (admin / super_admin — the server-side gateway only
 * joins those clients into the `system:alerts` room; TASK-457 widened it from
 * super-admin-only).
 *
 * v0.1.0 surface is intentionally minimal: callers pass a side-effect
 * handler. Proper toast / banner UI tracks in
 * `TASK-106a-followup-alert-ui`.
 */
export function useSystemAlertsSubscribe(handler?: SystemAlertHandler): void {
  const { socket } = useSocket();

  useEffect(() => {
    if (!socket) return undefined;

    const onAlert = (raw: unknown) => {
      const parsed = systemAlertEventSchema.safeParse(raw);
      if (!parsed.success) {
        reportInvalidEnvelope(WS_EVENT_SYSTEM_ALERT, parsed.error.issues);
        return;
      }
      handler?.(parsed.data);
    };

    socket.on(WS_EVENT_SYSTEM_ALERT, onAlert);
    return () => {
      socket.off(WS_EVENT_SYSTEM_ALERT, onAlert);
    };
  }, [socket, handler]);
}

export interface AccountEventHandler {
  (event: AccountEvent): void;
}

/**
 * Listen to `account.events`.
 *
 * Server-side, the gateway joins the client into `account:${accountId}` from
 * the JWT claims; we do NOT pass an account id from the FE, since that would
 * be an unauthenticated room request and the server rejects it by design.
 * The `accountId` argument exists so callers can gate the subscription to a
 * specific account (e.g. an account-card page that only cares about its own
 * events), filtering client-side on `event.payload`.
 *
 * v0.1.0: handler-only surface. Follow-up wires concrete UI in
 * `TASK-106a-followup-account-events-ui`.
 */
export function useAccountEventsSubscribe(
  handler?: AccountEventHandler,
): void {
  const { socket } = useSocket();

  useEffect(() => {
    if (!socket) return undefined;

    const onEvent = (raw: unknown) => {
      const parsed = accountEventSchema.safeParse(raw);
      if (!parsed.success) {
        reportInvalidEnvelope(WS_EVENT_ACCOUNT_EVENT, parsed.error.issues);
        return;
      }
      handler?.(parsed.data);
    };

    socket.on(WS_EVENT_ACCOUNT_EVENT, onEvent);
    return () => {
      socket.off(WS_EVENT_ACCOUNT_EVENT, onEvent);
    };
  }, [socket, handler]);
}

/**
 * Subscribe to `bulk.progress` + `bulk.complete` for a specific job id and
 * invalidate the cached job row whenever a frame arrives (Wave 2.66 FE,
 * TASK-049-followup).
 *
 * Until BE TASK-042-followup ships the gateway emitter, neither event fires —
 * `useBulkJobQuery`'s 2s `refetchInterval` keeps the progress UI alive. Once
 * the BE pushes, this hook collapses the latency from ~2s to one network RTT.
 * The payload contract is intentionally not pinned here (BE-2 owns the
 * schema); we only need the event NAME to know "go refetch the job".
 */
export function useBulkJobProgressSubscribe(jobId: string | null): void {
  const { socket } = useSocket();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!socket || !jobId) return undefined;

    const invalidate = (raw: unknown) => {
      // Best-effort job-id filter: BE payload SHOULD carry job_id; if it does
      // not, fall back to invalidating every job — cheap on the client side.
      const candidate = (raw as { job_id?: unknown })?.job_id;
      if (typeof candidate === 'string' && candidate !== jobId) return;
      void queryClient.invalidateQueries({
        queryKey: ['bulk-jobs', 'detail', jobId],
      });
    };

    socket.on(WS_EVENT_BULK_PROGRESS, invalidate);
    socket.on(WS_EVENT_BULK_COMPLETE, invalidate);
    return () => {
      socket.off(WS_EVENT_BULK_PROGRESS, invalidate);
      socket.off(WS_EVENT_BULK_COMPLETE, invalidate);
    };
  }, [socket, queryClient, jobId]);
}
